import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createCanteen } from "../../../redux/actions/canteenActions";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";
import {
  ArrowLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  Building2,
  Send,
  Bath,
  Eye,
  Wind,
  ClipboardCheck,
  Trash2,
  FileText,
  Shield,
  Home,
  Info,
  Camera,
} from "lucide-react";

const SectionFSanitation = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const { loading, error: apiError } = useSelector((state) => state.canteen);

  const surveyId = localStorage.getItem("survey_id") || "defaultSurvey";
  const [showCamera, setShowCamera] = useState(false);

  const [previewImages, setPreviewImages] = useState({
    f3Document: null,
    f4Document: null,
  });

  const [form, setForm] = useState({
    f1: "",
    f1Remarks: "",
    f2: "",
    f2Remarks: "",
    f3: "",
    f3Document: null,
    f3Remarks: "",
    f4: "",
    f4Document: null,
    f4Remarks: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const sectionData = localStorage.getItem("survey_section_f");
        if (sectionData) {
          const savedData = JSON.parse(sectionData);
          setForm(savedData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        const isPartial = determinePartialStatus();

        const storageData = { ...form };
        if (storageData.f3Document instanceof File)
          storageData.f3Document = "pending_upload";
        if (storageData.f4Document instanceof File)
          storageData.f4Document = "pending_upload";

        localStorage.setItem(
          "survey_section_f",
          JSON.stringify({
            ...storageData,
            isPartial: isPartial,
            lastSaved: new Date().toISOString(),
          }),
        );
        setSaveStatus(t.global.alert_saved_locally);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [form, t]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (!file) return;

      // Size validation (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Type validation
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type,
        }));
        return;
      }

      setForm((prev) => ({
        ...prev,
        [name]: file,
      }));

      // Create preview if image
      if (file.type.startsWith("image/")) {
        setPreviewImages((prev) => ({
          ...prev,
          [name]: URL.createObjectURL(file),
        }));
      }

      // Clear error
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        // Clear optional fields when answer changes
        ...(name === "f1" && value === t.global.yes && { f1Remarks: "" }),
        ...(name === "f2" && value === t.global.yes && { f2Remarks: "" }),
        ...(name === "f3" && value === t.global.yes && { f3Remarks: "" }),
        ...(name === "f3" && value === t.global.no && { f3Document: null }),
        ...(name === "f4" && value === t.global.yes && { f4Remarks: "" }),
        ...(name === "f4" && value === t.global.no && { f4Document: null }),
      }));
    }

    // Clear error when user starts typing/selecting
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // Determine if section is partially completed
  const determinePartialStatus = () => {
    // Check basic required fields
    if (!form.f1 || !form.f2 || !form.f3 || !form.f4) {
      return false;
    }
    return false;
  };

  const validateForm = () => {
    const newErrors = {};

    // F1 validation - Required selection only
    if (!form.f1?.trim()) {
      newErrors.f1 = t.global.err_a1;
    }

    // F2 validation - Required selection only
    if (!form.f2?.trim()) {
      newErrors.f2 = t.global.err_a1;
    }

    // F3 validation - Required selection only
    if (!form.f3?.trim()) {
      newErrors.f3 = t.global.err_a1;
    }

    // F4 validation - Required selection only
    if (!form.f4?.trim()) {
      newErrors.f4 = t.global.err_a1;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const yesNoToInt = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    return null;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.closest(".question-container")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("municipality_id", state?.municipality_id || 1);
      payload.append("ward_id", state?.ward_id || 5);
      payload.append("district_id", state?.district_id || 10);
      payload.append(
        "lattitude_longitude",
        state?.lattitude_longitude || "22.5726,88.3639",
      );
      payload.append("program_name", state?.area_name || "demo kiosk");
      payload.append("current_section", "sanitation");
      payload.append("kiosk_location_fulladdress", state?.fullAddress || "N/A");
      payload.append("reporting_date", new Date().toISOString().split("T")[0]);
      payload.append("area_id", state?.area_id || state?.id);
      payload.append("canteen_type_id", state?.canteen_type || 2);

      // Append form data
      payload.append("sanitation_F1", yesNoToInt(form.f1));
      payload.append("sanitation_F1_remarks", form.f1Remarks || "");

      payload.append("sanitation_F2", yesNoToInt(form.f2));
      payload.append("sanitation_F2_remarks", form.f2Remarks || "");

      payload.append("sanitation_F3", yesNoToInt(form.f3));
      payload.append("sanitation_F3_remarks", form.f3Remarks || "");

      if (form.f3Document) {
        payload.append("sanitation_F3_upload", form.f3Document);
      }

      payload.append("sanitation_F4", yesNoToInt(form.f4));
      payload.append("sanitation_F4_remarks", form.f4Remarks || "");

      if (form.f4Document) {
        payload.append("sanitation_F4_upload", form.f4Document);
      }

      // Determine partial status
      const isPartial = determinePartialStatus();

      // Save section data with partial flag
      const sectionDataWithPartial = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        submittedAsPartial: isPartial,
      };

      // Always save the data
      localStorage.setItem(
        "survey_section_f",
        JSON.stringify(sectionDataWithPartial),
      );

      // Update completed sections tracking
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      if (!isPartial) {
        completedSections.f = true;
      } else {
        delete completedSections.f;
      }
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Store partial sections separately
      const partialSections = JSON.parse(
        localStorage.getItem("partialSections") || "{}",
      );
      if (isPartial) {
        partialSections.f = true;
      } else {
        delete partialSections.f;
      }
      localStorage.setItem("partialSections", JSON.stringify(partialSections));

      // Dispatch Redux action
      const response = await dispatch(createCanteen(payload));

      // Navigate back to sections
      navigate("/survey/sections", { state });
    } catch (err) {
      console.error("Error submitting Section F:", err);
      alert(t.global.alert_server_error);
      setIsSubmitting(false);
    }
  };

  const yesNoOptions = [t.global.yes, t.global.no];

  const getSurveyTypeDisplay = (type) => {
    switch (type) {
      case "kiosk":
        return t.global.survey_type_kiosk;
      case "kitchen":
        return t.global.survey_type_kitchen;
      case "kitchencumDining":
        return t.global.survey_type_kitchendining;
      default:
        return type || t.global.survey_type_default;
    }
  };

  // Calculate if form can be submitted
  const isFormSubmittable = form.f1 && form.f2 && form.f3 && form.f4;

  // Check if form will be submitted as partial
  const willBePartial = isFormSubmittable ? determinePartialStatus() : false;

  // Show completion status only when form is submittable
  const showCompletionStatus = isFormSubmittable;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kiosk.sections.F.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kiosk.sections.F.subtitle}
              </p>
              {saveStatus && (
                <div className="text-sm text-green-600 mt-1 flex items-center">
                  <CheckCircle size={14} className="mr-1" />
                  {saveStatus}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full flex items-center">
                <Building2 size={14} className="mr-1" />
                {getSurveyTypeDisplay(state?.surveyType)} {t.global.survey_type_default}
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          {/* Partial Submission Warning */}
          {showCompletionStatus && willBePartial && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="text-amber-500 mr-3 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-800">
                    Partial Submission
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* API Error */}
          {apiError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="text-red-500 mr-3" size={20} />
              <div>
                <p className="font-medium text-red-800">{t.global.error_title}</p>
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* F1 - Dedicated Functional Toilet */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Bath size={18} className="mr-2" />
                F1. {t.modules.kiosk.sections.F.questions.staffToilet}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4 flex items-center">
                <Shield size={14} className="mr-1" />
                {t.modules.kiosk.sections.F.hints.staffToilet}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.f1 === option
                          ? form.f1 === t.global.yes
                            ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                            : "bg-red-50 border-red-300 ring-2 ring-red-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="f1"
                            value={option}
                            checked={form.f1 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.f1 === option
                                ? form.f1 === t.global.yes
                                  ? "border-green-500 bg-green-500"
                                  : "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.f1 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f1}
                </p>
              )}

              {form.f1 === t.global.yes && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium flex items-center">
                    <CheckCircle size={14} className="mr-1" />
                    Dedicated functional toilet is available for staff.
                  </p>
                </div>
              )}
            </div>

            {/* F2 - Toilet Ventilation & Privacy */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Wind size={18} className="mr-2" />
                F2. {t.modules.kiosk.sections.F.questions.toiletConditions}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4 flex items-center">
                <Eye size={14} className="mr-1" />
                {t.modules.kiosk.sections.F.hints.toiletConditions}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.f2 === option
                          ? form.f2 === t.global.yes
                            ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                            : "bg-red-50 border-red-300 ring-2 ring-red-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="f2"
                            value={option}
                            checked={form.f2 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.f2 === option
                                ? form.f2 === t.global.yes
                                  ? "border-green-500 bg-green-500"
                                  : "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.f2 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f2}
                </p>
              )}

              {form.f2 === t.global.yes && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium flex items-center">
                    <CheckCircle size={14} className="mr-1" />
                    Toilets have proper ventilation, illumination, and privacy.
                  </p>
                </div>
              )}
            </div>

            {/* F3 - Daily Cleaning & Recording */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <ClipboardCheck size={18} className="mr-2" />
                F3. {t.modules.kiosk.sections.F.questions.toiletCleaning}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.f3 === option
                          ? form.f3 === t.global.yes
                            ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                            : "bg-red-50 border-red-300 ring-2 ring-red-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="f3"
                            value={option}
                            checked={form.f3 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.f3 === option
                                ? form.f3 === t.global.yes
                                  ? "border-green-500 bg-green-500"
                                  : "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.f3 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f3}
                </p>
              )}

              {form.f3 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <label className="block text-sm font-medium mb-4 text-gray-700 flex items-center">
                    <FileText size={14} className="mr-2" />
                    Upload housekeeping checklist document ({t.global.optionalNote?.split(":")[0]}):
                    <span className="text-gray-500 ml-1 italic text-xs">
                      (Timestamp & GPS required for full verification)
                    </span>
                  </label>

                  {/* SELECTION BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => setShowCamera(true)}
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-green-800 active:text-white transition-all duration-150 ease-out cursor-pointerfont-medium"
                    >
                      <Camera size={20} /> {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="f3Document"
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-green-800 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Upload size={20} /> {t.global.upload_file}
                    </label>
                  </div>

                  {/* HIDDEN FILE INPUT */}
                  <input
                    type="file"
                    name="f3Document"
                    onChange={handleChange}
                    className="hidden"
                    id="f3Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    disabled={isSubmitting}
                  />
                  {/* PREVIEW BOX */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      form.f3Document
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    {form.f3Document ? (
                      <div className="space-y-4">
                        {/* IMAGE PREVIEW */}
                        {previewImages?.f3Document &&
                          form.f3Document.type?.startsWith("image/") && (
                            <img
                              src={previewImages.f3Document}
                              alt="Preview"
                              className="max-h-56 mx-auto rounded-lg shadow"
                            />
                          )}

                        <div className="flex items-center justify-between">
                          <div className="text-left">
                            <p className="text-sm break-all font-medium">
                              {form.f3Document.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {form.f3Document.type}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              if (previewImages?.f3Document) {
                                URL.revokeObjectURL(previewImages.f3Document);
                              }

                              setForm((prev) => ({
                                ...prev,
                                f3Document: null,
                              }));

                              setPreviewImages((prev) => ({
                                ...prev,
                                f3Document: null,
                              }));
                            }}
                            className="text-red-600 text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        {t.global.capture_hint} checklist document
                      </p>
                    )}
                  </div>

                  {errors.f3Document && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />{" "}
                      {errors.f3Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* F4 - Covered Dustbin for Sanitary Waste */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Trash2 size={18} className="mr-2" />
                F4. {t.modules.kiosk.sections.F.questions.sanitaryBin}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kiosk.sections.F.hints.sanitaryBin}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.f4 === option
                          ? form.f4 === t.global.yes
                            ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                            : "bg-red-50 border-red-300 ring-2 ring-red-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="f4"
                            value={option}
                            checked={form.f4 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.f4 === option
                                ? form.f4 === t.global.yes
                                  ? "border-green-500 bg-green-500"
                                  : "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.f4 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f4}
                </p>
              )}

              {form.f4 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border border-green-300 rounded-lg">
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Evidence of covered dustbin ({t.global.optionalNote?.split(":")[0]}):
                    <span className="text-gray-500 ml-1 italic text-xs">
                      (Timestamp & GPS required for full verification)
                    </span>
                  </label>

                  {/* SELECTION BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => setShowCamera(true)}
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-green-800 active:text-white transition-all duration-150 ease-out cursor-pointerfont-medium"
                    >
                      <Camera size={20} /> {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="f4Document"
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-green-800 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Upload size={20} /> {t.global.upload_file}
                    </label>
                  </div>

                  {/* HIDDEN FILE INPUT */}
                  <input
                    type="file"
                    name="f4Document"
                    onChange={handleChange}
                    className="hidden"
                    id="f4Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    disabled={isSubmitting}
                  />

                  {/* PREVIEW BOX */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      form.f4Document
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    {form.f4Document ? (
                      <div className="space-y-4">
                        {previewImages?.f4Document &&
                          form.f4Document.type?.startsWith("image/") && (
                            <img
                              src={previewImages.f4Document}
                              alt="Preview"
                              className="max-h-56 mx-auto rounded-lg shadow"
                            />
                          )}

                        <div className="flex items-center justify-between">
                          <div className="text-left">
                            <p className="text-sm break-all font-medium">
                              {form.f4Document.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {form.f4Document.type}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              if (previewImages?.f4Document) {
                                URL.revokeObjectURL(previewImages.f4Document);
                              }

                              setForm((prev) => ({
                                ...prev,
                                f4Document: null,
                              }));

                              setPreviewImages((prev) => ({
                                ...prev,
                                f4Document: null,
                              }));
                            }}
                            className="text-red-600 text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        {t.global.capture_hint} dustbin evidence
                      </p>
                    )}
                  </div>

                  {errors.f4Document && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />{" "}
                      {errors.f4Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Completion Status - Only show when form is submittable */}
            {showCompletionStatus && (
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Info size={18} className="mr-2 text-blue-600" />
                    <span className="font-medium text-gray-700">
                      Submission Status:
                    </span>
                  </div>
                  <div>
                    {willBePartial ? (
                      <span className="text-amber-600 font-medium">
                        Will be Partial
                      </span>
                    ) : (
                      <span className="text-green-600 font-medium">
                        Will be Complete
                      </span>
                    )}
                  </div>
                </div>

                {willBePartial && (
                  <div className="mt-2 text-sm text-amber-700">
                    <p>
                      Missing optional information will mark this section as
                      partially completed.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-6 border-t space-y-3">
              {/* Buttons Row */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => navigate("/survey/sections", { state })}
                  className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  <ArrowLeft size={18} className="mr-2" />
                  {t.global.back_to_sections}
                </button>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isFormSubmittable}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                    isSubmitting || !isFormSubmittable
                      ? "bg-blue-400 text-white cursor-not-allowed"
                      : willBePartial
                        ? "bg-amber-500 text-white hover:bg-amber-600"
                        : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t.global.submitting}
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      {t.global.submit_section_f}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-600 font-medium">
                  {t.global.mandatoryFields}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            if (!file) return;

            setForm((prev) => ({
              ...prev,
              f3Document: file,
              f4Document: file,
            }));

            if (file.type.startsWith("image/")) {
              setPreviewImages((prev) => ({
                ...prev,
                f3Document: URL.createObjectURL(file),
                f4Document: URL.createObjectURL(file),
              }));
            }

            setShowCamera(false);
          }}
        />
      )}
    </div>
  );
};

export default SectionFSanitation;